<template>
    <li v-on="$listeners">
        <b-avatar
            :badge="chat.type === 'one_to_one'"
            :badge-variant="
                otherParticipant.user && !otherParticipant.user.pictureUrl
                    ? resolveUserStatus(otherParticipant.user.status)
                    : undefined
            "
            :src="chat.type === 'one_to_one' && otherParticipant.user ? otherParticipant.user.pictureUrl : undefined"
            :text="
                resolveInitials(
                    chat.type === 'one_to_one' && otherParticipant.user ? otherParticipant.user.name : chat.name
                )
            "
            badge-offset="-3px"
            class="badge-team-chat round-large"
            size="34"
        />

        <div class="chat-info flex-grow-1 mt-0">
            <h5 class="mb-0 text-truncate">
                <strong :class="{ 'text-secondary': chat.isArchived }">
                    {{ resolveChatTitle() }}
                </strong>
            </h5>
            <p class="card-text text-truncate">
                <font-awesome-icon
                    v-if="hasMessage && chat.lastMessage.type === 'asset'"
                    :icon="resolveEntryIcon(chat.lastMessage)"
                    class="mr-50"
                />

                <span v-if="hasMessage && chat.lastMessage.type === 'asset'" class="text-secondary">
                    {{ $t(resolveLastEntryMessage(chat.lastMessage)) }}
                </span>
                <span v-else class="text-secondary">
                    <template v-if="hasMessage && !isTyping && chat.lastMessage.type !== 'event'">
                        {{ chat.lastMessage.message }}
                    </template>
                    <template v-if="hasMessage && !isTyping && chat.lastMessage.type === 'event'">
                        {{ displayEvent }}
                    </template>
                    <template v-if="isTyping">
                        {{ getUsersTyping(chat) }}
                        {{ $tc("team-chats.typing", isMoreThanOneTyping ? 0 : 1) }}...
                    </template>
                </span>
            </p>
        </div>
        <div class="chat-meta text-right d-flex justify-content-end">
            <small
                v-if="hasMessage"
                :class="showUnreadMessageBadge() ? 'text-primary' : 'text-secondary'"
                class="chat-time mb-25"
            >
                {{ timeAgoValue }}
            </small>
            <b-badge
                v-if="showUnreadMessageBadge() && resolveUnreadMessagesCount().length > 0"
                class="ml-25"
                pill
                variant="messenger"
            >
                <b>{{ resolveUnreadMessagesCount() }}</b>
            </b-badge>
        </div>
    </li>
</template>

<script>
import { BAvatar, BBadge } from "bootstrap-vue";
import { timeAgo } from "@/utils/luxon";
import { resolveInitials, resolveUserStatus } from "@/utils/users";
import { resolveEntryIcon } from "@/utils/chats";
import {
    getChannelSettingValue,
    resolveLastEntryMessage,
    resolvePlatformIcon,
    resolvePriorityVariant,
    resolveProviderColor,
} from "@/utils/channels";
import i18n from "@/libs/i18n";
import useActiveTeamChat from "../useActiveTeamChat";

export default {
    components: {
        BAvatar,
        BBadge,
    },
    props: {
        chat: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            timer: null,
            timeAgoValue: "",
        };
    },
    computed: {
        isTyping() {
            return this.chat?.usersTyping?.length > 0;
        },
        isMoreThanOneTyping() {
            return this.chat?.usersTyping?.length > 1;
        },
        hasMessage() {
            return Boolean(this.chat?.lastMessage?.id);
        },
        otherParticipant() {
            return this.chat.participants.find((participant) => participant.userId !== this.user.id);
        },
        currentParticipant() {
            return this.chat.participants.find((participant) => participant.userId === this.user.id);
        },
        displayEvent() {
            const data = this.parseEvent(this.chat.lastMessage.message);
            return i18n.t(`team-chats.events.${data?.text}`, {
                participant: data?.participant,
                addedBy: data?.addedBy,
            });
        },
    },
    created() {
        this.timer = setInterval(() => {
            if (this.chat.lastMessage) {
                this.timeAgoValue = timeAgo(this.chat.lastMessage.createdAt);
            }
        }, 1000);
    },
    methods: {
        resolveUnreadMessagesCount() {
            if (!this.chat.unreadMessages && this.currentParticipant.chatStatus === "unread") {
                return "";
            }

            return this.chat.unreadMessages;
        },
        showUnreadMessageBadge() {
            return this.chat.unreadMessages > 0 || this.currentParticipant.chatStatus === "unread";
        },
        resolveChatTitle() {
            var archivedSufix = this.chat.isArchived ? ` (${this.$t("team-chats.archived")})` : "";
            var chatName =
                this.chat.type === "one_to_one" && this.otherParticipant.user
                    ? this.otherParticipant.user.name
                    : this.chat.name;

            return chatName + archivedSufix;
        },
        findName(userId) {
            if (!Array.isArray(this.chat.participants)) {
                return "";
            }

            const participant = this.chat.participants.find((participant) => participant.userId === userId);

            return participant?.user?.name ?? "";
        },
        parseEvent(message) {
            const parsedMessage = JSON.parse(message);
            switch (parsedMessage.eventType) {
                case "TeamChat::participantLeft":
                    return {
                        participant: this.findName(parsedMessage.payload.userId),
                        text: "participant-left",
                    };
                case "TeamChat::participantAdded":
                    return {
                        participant: this.findName(parsedMessage.payload.userId),
                        addedBy: this.findName(parsedMessage.payload.addedBy),
                        text: "participant-added",
                    };
                case "TeamChat::participantRemoved":
                    return {
                        participant: this.findName(parsedMessage.payload.userId),
                        text: "participant-removed",
                    };
                case "TeamChat::chatDeleted":
                    return {
                        text: "chat-deleted",
                    };
                case "TeamChat::groupChatCreated":
                    return {
                        text: "group-chat-created",
                    };
            }
        },
    },
    beforeUnmount() {
        clearInterval(this.timer);
    },
    setup() {
        const { getUsersTyping } = useActiveTeamChat();

        return {
            resolveUserStatus,
            resolveProviderColor,
            resolvePlatformIcon,
            timeAgo,
            getChannelSettingValue,
            resolvePriorityVariant,
            resolveEntryIcon,
            resolveLastEntryMessage,
            resolveInitials,
            getUsersTyping,
        };
    },
};
</script>
